// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { defaults as addressDefaults } from '@polkadot/util-crypto/address/defaults';
import { keyring } from '@polkadot/ui-keyring';
import { web3Accounts, web3Enable } from '@polkadot/extension-dapp';
// import {formatBalance, isTestChain} from "@polkadot/util";
// import StatusContext from "Context/Queue";
import { WsProvider } from '@polkadot/rpc-provider';
import { ApiPromise } from '@polkadot/api/promise';
// import {Detector} from "@substrate/connect";
import { useSetRecoilState } from 'recoil';
import { allAccounts, polkaService } from 'store/app';
import { decodeUrlTypes } from 'utils/urlTypes';
import registry from 'utils/typeRegistry';
import ApiContext from './ApiContext';
import types from 'types/types.json';

export const ethereumChains = [
  'moonbase',
  'moonbeam',
  'moonriver',
  'moonshadow',
  'origintrail-parachain',
];

export const DEFAULT_DECIMALS = registry.createType('u32', 18);
export const DEFAULT_SS58 = registry.createType('u32', addressDefaults.prefix);
export const DEFAULT_AUX = [
  'Aux1',
  'Aux2',
  'Aux3',
  'Aux4',
  'Aux5',
  'Aux6',
  'Aux7',
  'Aux8',
  'Aux9',
];

let api;

function isKeyringLoaded() {
  try {
    return !!keyring.keyring;
  } catch {
    return false;
  }
}

function getDevTypes() {
  const types = decodeUrlTypes();
  const names = Object.keys(types);

  names.length && console.log('Injected types:', names.join(', '));

  return types;
}

async function getInjectedAccounts(injectedPromise) {
  try {
    await injectedPromise;

    const accounts = await web3Accounts();

    //console.log(accounts);

    return accounts.map(({ address, meta }, whenCreated) => ({
      address,
      meta: {
        ...meta,
        name: `${meta.name || 'unknown'} (${
          meta.source === 'polkadot-js' ? 'extension' : meta.source
        })`,
        whenCreated,
      },
    }));
  } catch (error) {
    console.error('web3Accounts', error);

    return [];
  }
}

async function retrieve(api, injectedPromise) {
  const [
    chainProperties,
    systemChain,
    systemChainType,
    systemName,
    systemVersion,
    injectedAccounts,
  ] = await Promise.all([
    api.rpc.system.properties(),
    api.rpc.system.chain(),
    api.rpc.system.chainType
      ? api.rpc.system.chainType()
      : Promise.resolve(registry.createType('ChainType', 'Live')),
    api.rpc.system.name(),
    api.rpc.system.version(),
    getInjectedAccounts(injectedPromise),
  ]);

  return {
    injectedAccounts,
    properties: registry.createType('ChainProperties', {
      ss58Format: api.consts.system?.ss58Prefix || chainProperties.ss58Format,
      tokenDecimals: chainProperties.tokenDecimals,
      tokenSymbol: chainProperties.tokenSymbol,
    }),
    systemChain: (systemChain || '<unknown>').toString(),
    systemChainType,
    systemName: systemName.toString(),
    systemVersion: systemVersion.toString(),
  };
}

async function loadOnReady(api, injectedPromise, store, types) {
  // registry.register(types);
  //console.log('api', api);
  const {
    injectedAccounts,
    properties,
    systemChain,
    systemChainType,
    systemName,
    systemVersion,
  } = await retrieve(api, injectedPromise);

  //console.log('injectedAccounts', injectedAccounts);
  //     const ss58Format = settings.prefix === -1
  //         ? properties.ss58Format.unwrapOr(DEFAULT_SS58).toNumber()
  //         : settings.prefix;
  //     const tokenSymbol = properties.tokenSymbol.unwrapOr([formatBalance.getDefaults().unit, ...DEFAULT_AUX]);
  //     const tokenDecimals = properties.tokenDecimals.unwrapOr([DEFAULT_DECIMALS]);
  const isEthereum = ethereumChains.includes(
    api.runtimeVersion.specName.toString(),
  );
  const isDevelopment =
    systemChainType.isDevelopment || systemChainType.isLocal;
  //
  //     //console.log(`chain: ${systemChain} (${systemChainType.toString()}), ${JSON.stringify(properties)}`);
  //
  //     // explicitly override the ss58Format as specified
  //     registry.setChainProperties(registry.createType('ChainProperties', { ss58Format, tokenDecimals, tokenSymbol }));
  //
  //     // first setup the UI helpers
  //     formatBalance.setDefaults({
  //         decimals: (tokenDecimals).map((b) => b.toNumber()),
  //         unit: tokenSymbol[0].toString()
  // });
  // TokenUnit.setAbbr(tokenSymbol[0].toString());

  // finally load the keyring
  isKeyringLoaded() ||
    keyring.loadAll(
      {
        genesisHash: api.genesisHash,
        // isDevelopment,
        // ss58Format,
        // store,
        type: isEthereum ? 'ethereum' : 'ed25519',
      },
      injectedAccounts,
    );

  const defaultSection = Object.keys(api.tx)[0];
  const defaultMethod = Object.keys(api.tx[defaultSection])[0];
  const apiDefaultTx = api.tx[defaultSection][defaultMethod];
  const apiDefaultTxSudo =
    (api.tx.system && api.tx.system.setCode) || apiDefaultTx;

  // setDeriveCache(api.genesisHash.toHex(), deriveMapCache);

  return {
    apiDefaultTx,
    apiDefaultTxSudo,
    hasInjectedAccounts: injectedAccounts.length !== 0,
    isApiReady: true,
    isDevelopment: isEthereum ? false : isDevelopment,
    isEthereum,
    specName: api.runtimeVersion.specName.toString(),
    specVersion: api.runtimeVersion.specVersion.toString(),
    systemChain,
    systemName,
    systemVersion,
    injectedAccounts,
  };
}

function Api({ apiUrl, children }) {
  // const { queuePayload, queueSetTxStatus } = useContext(StatusContext);
  const [state, setState] = useState({
    hasInjectedAccounts: false,
    isApiReady: false,
  });
  const [isApiConnected, setIsApiConnected] = useState(false);
  const [isApiInitialized, setIsApiInitialized] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [extensions, setExtensions] = useState();
  const setPolkaService = useSetRecoilState(polkaService);
  const setAllAccounts = useSetRecoilState(allAccounts);

  const value = useMemo(
    () => ({
      ...state,
      api,
      apiError,
      apiUrl,
      extensions,
      isApiConnected,
      isApiInitialized,
      isWaitingInjected: !extensions,
    }),
    [apiError, extensions, isApiConnected, isApiInitialized, state, apiUrl],
  );

  // initial initialization
  useEffect(() => {
    let provider;

    if (apiUrl.startsWith('light://')) {
      // const detect = new Detector('polkadot-js/apps');
      // provider = detect.provider({ name: apiUrl.replace('light://substrate-connect/', ''), spec: '' });
      // provider.connect().catch(console.error);
    } else {
      provider = new WsProvider(apiUrl);
    }

    // const signer = new ApiSigner(registry, queuePayload, queueSetTxStatus);
    // const types = getDevTypes();

    ApiPromise.create({
      provider,
      typesAlias: {
        tokens: {
          AccountData: 'OrmlAccountData',
        },
      },
      types,
    }).then(resApi => {
      api = resApi;
      //console.log('resApi', resApi);
      // api.on('ready', (): void => {
      const injectedPromise = web3Enable('polkadot-js/apps');

      injectedPromise.then(setExtensions).catch(console.error);

      loadOnReady(api, injectedPromise)
        .then(setState)
        .catch(error => {
          console.error(error);

          setApiError(error.message);
        });
      // });
    });

    setIsApiInitialized(true);
  }, [apiUrl]);

  useEffect(() => {
    setPolkaService({
      ...state,
      api,
      apiError,
      apiUrl,
      extensions,
      isApiConnected,
      isApiInitialized,
      isWaitingInjected: !extensions,
    });
    setAllAccounts(state.injectedAccounts);
  }, [
    apiError,
    extensions,
    isApiConnected,
    isApiInitialized,
    state,
    apiUrl,
    api,
  ]);

  if (!value.isApiInitialized) {
    return null;
  }

  return <React.Fragment>{children}</React.Fragment>;
}

export default React.memo(Api);
